// Sugalarm — cycle 412: the number an alarm surface shows while the
// crossing's reading frame is still in flight.
//
// The alarm engine sends the `alert` frame from INSIDE `evaluate_reading`
// and the `glucose` frame only after the whole fan-out (which places the
// Twilio call synchronously, ~2 s). For that window a takeover mounted on
// the alert frame said "URGENT LOW" over the PREVIOUS reading's number —
// hardware acceptance on 2026-09-01 caught a frame reading "URGENT LOW · 85"
// one second after a 58 had crossed. The alert row already carries the
// crossing value (`alerts.sgv`), so the surface prefers it whenever the
// row is NEWER than the reading the device holds; the moment the reading
// frame lands (same timestamp) the live reading takes over again. Pure.
//
// ⚠ BOUNDED, on the safety review's F4: the substitution covers a fan-out
// GAP, not a dark feed. Without a bound a device holding an open row and no
// newer reading would paint the crossing number for hours with no staleness
// treatment. So (1) with NO reading at all the surface shows what it always
// showed (nothing) — the hero's "No Data" is the truth there — and (2) the
// row's value is trusted only while a reading of the same age would still
// be fresh (`ALARM_DISPLAY_SGV_MAX_AGE_MS`); past that the live (stale)
// reading and its staleness treatment take over again, exactly as before
// this cycle.

export interface AlarmRowForDisplay {
  sgv: number;
  triggered_at: string;
  fired_at: string | null;
  recovered_at?: string | null;
}

/** How long the row's crossing value may stand in for a reading the device
 *  has not received yet — the platform's own freshness window, so the alarm
 *  surface is never fresher than the hero would be for a reading that age. */
export const ALARM_DISPLAY_SGV_MAX_AGE_MS = 10 * 60_000;

export function alarmDisplaySgv(
  readingSgv: number | null,
  readingTsMs: number | null,
  row: AlarmRowForDisplay | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!row || row.fired_at == null || row.recovered_at != null) return readingSgv;
  if (readingTsMs == null) return readingSgv;
  const rowMs = Date.parse(row.triggered_at);
  if (!Number.isFinite(rowMs)) return readingSgv;
  if (rowMs <= readingTsMs) return readingSgv;
  if (nowMs - rowMs > ALARM_DISPLAY_SGV_MAX_AGE_MS) return readingSgv;
  return row.sgv;
}
